import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Chip,
  Typography,
  Tooltip,
  useTheme,
  Stack,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import axiosInstance from "../services/axiosInstance";
import { SA_GET_PARENT_PERMISSIONS } from "../shared/constants/apiEndpoints";
import AgGridTable from "../components/table/AgGridTable";
import SkeletonLoader from "../components/skeleton_loader";

interface PermissionItem {
  id?: string;
  name?: string;
  code?: string;
  permission_name?: string;
  permission_code?: string;
  parent_id?: string | null;
  role_defaults?: Record<string, boolean>;
  created_at?: string;
}

const RolePermission: React.FC = () => {
  const [permissionsData, setPermissionsData] = useState<PermissionItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(SA_GET_PARENT_PERMISSIONS);
      const data = response?.data?.data || response?.data || [];
      setPermissionsData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching parent permissions:", error);
      setPermissionsData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const columnDefs = [
    {
      headerName: "ID",
      field: "id",
      minWidth: 280,
      cellStyle: { display: "flex", alignItems: "center" },
      cellRenderer: (params: any) => {
        const val = params.value || params.data?.parent_id || "-";
        return (
          <Tooltip title={val} arrow>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: "13px",
                color: isDarkMode ? "#F8FAFC" : "#1E293B",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {val}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Permission Name",
      field: "name",
      minWidth: 220,
      flex: 1,
      cellStyle: { display: "flex", alignItems: "center" },
      cellRenderer: (params: any) => {
        const val = params.data?.name || params.data?.permission_name || "-";
        return (
          <Tooltip title={val} arrow>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: isDarkMode ? "#F8FAFC" : "#1E293B",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {val}
            </Typography>
          </Tooltip>
        );
      },
    },
{
  headerName: "Permission Code",
  field: "code",
  minWidth: 200,
  flex: 1,
  cellStyle: {
    display: "flex",
    alignItems: "center",
  },
  cellRenderer: (params: any) => {
    const val = params.data?.code || params.data?.permission_code || "-";

    return (
      <Chip
        label={val}
        size="small"
        variant="outlined"
        sx={{
          fontFamily: "'Fira Code', monospace",
          fontWeight: 600,
          fontSize: "12px",
          borderRadius: "6px",
          height: 28,

          bgcolor: isDarkMode
            ? "rgba(99,102,241,.18)"
            : "rgba(79,70,229,.10)",

          color: isDarkMode ? "#A5B4FC" : "#4338CA",

          border: "1px solid",
          borderColor: isDarkMode
            ? "rgba(99,102,241,.4)"
            : "rgba(79,70,229,.25)",

          "&:hover": {
            bgcolor: isDarkMode
              ? "rgba(99,102,241,.28)"
              : "rgba(79,70,229,.16)",
          },
        }}
      />
    );
  },
},
{
  headerName: "Action",
      field: "action",
      width: 90,
      minWidth: 90,
      maxWidth: 100,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      cellRenderer: (params: any) => {
        const parentId = params.data?.id;
        return (
          <Tooltip title="Add Child Permission" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (params.context?.openCreateDrawerWithParent) {
                  params.context.openCreateDrawerWithParent(parentId);
                }
              }}
              sx={{
                width: 30,
                height: 30,
                color: isDarkMode ? "#818CF8" : "#4F46E5",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: isDarkMode ? "rgba(99, 102, 241, 0.2)" : "rgba(79, 70, 229, 0.12)",
                  transform: "scale(1.15)",
                  color: isDarkMode ? "#A5B4FC" : "#4338CA",
                },
              }}
            >
              <AddIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Card
        variant="outlined"
        sx={{
          height: "calc(100vh - 103px)",
          borderRadius: 3,
          width: "100%",
        }}
      >
        {loading ? (
          <Box
            sx={{
              width: "100%",
              height: "calc(88vh - 90px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <SkeletonLoader
              count={Math.floor(window.innerHeight / 40)}
              styles={{ height: 30, width: "100%" }}
            />
          </Box>
        ) : (
          <AgGridTable
            columnDefs={columnDefs}
            data={permissionsData}
            showSearch
            searchPlaceholder="Search permissions..."
            height="calc(91.5vh - 90px)"
            isLoading={loading}
            onInviteSuccess={fetchPermissions}
            InviteTableRefresh={fetchPermissions}
          />
        )}
      </Card>
    </Box>
  );
};

export default RolePermission;
